import React from 'react';
import { ZillowInsuranceServicesLogo } from '@zillow/zrs-comet';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Container from '@zg-rentals/multibrand-ui/lib/Container';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Checkbox from '@zg-rentals/multibrand-ui/lib/Checkbox';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import ConfirmStopPayments from '@rental-manager/pages/Dashboard/EditPaymentLengthModal/modules/ConfirmStopPayments';
import Or from '@common/components/Or';
import Section from '@rental-manager/components/Section';
import HelpToolTip from '@rental-manager/pages/Onboarding//HelpToolTip';
import Modal from '@common/components/Modal';
import FaqComponent from '@common/components/FaqComponent';
import SpinnerContainer from '@common/components/SpinnerContainer';
import FaqConstants from '@rental-manager/constants/FaqConstants';
import userPersonas from '@common/constants/userPersonas';
import { isStartDateTooSoon, makeDueDates, STORE_DATE_FORMAT } from '@common/paymentsUtils/selectOptionsBuilder';
import type { EndDatesInterface, StartDatesInterface } from '@common/types';
import type { IRentTermsModel } from '@rental-manager/models/Types';
import './index.scss';
import { ACTIVE, PENDING_ACTIVATION } from '@rental-manager/constants/RentalProtectionPolicyStatusConstants';
import DebouncedButton from '@common/components/DebouncedButton';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

type Props = {
  today: Date;
  firstDueDate: string;
  paymentDateForFirstMonth: string;
  numberOfPayments: number;
  monthToMonth: boolean;
  paymentDayOfTheMonth: number;
  rentTermsKey: string;
  onSubmit: any;
  onClose: () => void;
  onCancel: () => void;
  onBack: () => void;
  requestStopPayments: (rentTermsKey: string) => any;
  currentTermRentTermsKey: string;
  rentalProtectionPolicyStatus: string;
  listingAlias: string;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
};

type State = {
  startDates: Array<StartDatesInterface>;
  selectedStartDate: StartDatesInterface;
  selectedEndDate: EndDatesInterface;
  monthToMonth: boolean;
  isLoading: boolean;
  showConfirmStopPayments: boolean;
};

// FIXME: This is a dupe of EditPaymentLengthModal and NewTenantTermLength components
class EditTermLengthModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      firstDueDate,
      paymentDateForFirstMonth,
      numberOfPayments,
      monthToMonth,
      paymentDayOfTheMonth,
      currentTermRentTermsKey,
      rentTermsKey,
      today,
    } = this.props;
    const dateForMakingDueDate = rentTermsKey === currentTermRentTermsKey ? today : firstDueDate;
    const startDates: Array<StartDatesInterface> = makeDueDates(paymentDayOfTheMonth, dateForMakingDueDate);

    const selectedStartDate =
      startDates.find((startDate) => startDate.date === formatDate(paymentDateForFirstMonth, STORE_DATE_FORMAT)) ||
      startDates[0];

    const selectedEndDate =
      selectedStartDate.endDates.find((endDate) => endDate.payments === numberOfPayments) ||
      selectedStartDate.endDates[0];
    this.state = {
      startDates,
      selectedStartDate,
      selectedEndDate,
      monthToMonth,
      isLoading: false,
      showConfirmStopPayments: false,
    };
  }

  onChangeMonthToMonth = (): void => {
    this.setState({
      monthToMonth: !this.state.monthToMonth,
    });
  };

  onSubmit = (): Promise<void> => {
    const { paymentDayOfTheMonth, onSubmit, currentProperty, user } = this.props;
    const { selectedStartDate, selectedEndDate, monthToMonth } = this.state;
    return Promise.resolve()
      .then(() => this.setState({ isLoading: true }))
      .then(() => {
        const paymentSettings: Partial<IRentTermsModel> = {
          firstDueDate: selectedStartDate.date,
          monthToMonth,
          dueLastDayOfMonth: paymentDayOfTheMonth === 31,
          rentTermsKey: this.props.rentTermsKey,
        };
        if (!monthToMonth) {
          paymentSettings.numberOfPayments = selectedEndDate.payments;
          paymentSettings.paymentEndDate = selectedEndDate.date;
        }
        return onSubmit(paymentSettings);
      })
      .then((response = {}) => {
        if (response.error) {
          this.setState({ isLoading: false });
        } else {
          // the tracking details will be replaced when we migrate dashboard events- ticket here- https://zillowgroup.atlassian.net/browse/TPAY-6994
          IreneOnboardingCSEvents.emitContinueFromTermLengthPageEvent({ currentProperty, user });
        }
      });
  };

  onShowConfirmStopPayments = (): void => {
    this.setState({
      showConfirmStopPayments: true,
    });
  };

  onConfirmStopPayments = (): Promise<void> =>
    Promise.resolve()
      .then(() => this.setState({ isLoading: true }))
      .then(() => this.props.requestStopPayments(this.props.rentTermsKey))
      .then(({ err } = {}) => {
        if (err) {
          this.setState({ isLoading: false });
        }
      });

  onCloseStopPayments = (): Promise<void> =>
    Promise.resolve().then(() => this.setState({ showConfirmStopPayments: false }));

  onCancel = (): void => {
    this.props.onCancel();
  };

  onClose = (): void => {
    this.props.onClose();
  };

  gotoPreviousPage = (): void => {
    this.props.onBack();
  };

  onChangeStartDate = (e: any): void => {
    const { startDates } = this.state;
    const selectedStartDate = startDates[e.currentTarget.selectedIndex];
    this.setState({
      selectedStartDate,
    });
  };

  onChangeEndDate = (e: any): void => {
    const { selectedStartDate } = this.state;
    const selectedEndDate = selectedStartDate.endDates[e.currentTarget.selectedIndex];
    this.setState({
      selectedEndDate,
    });
  };

  renderStartDateOptions = (): React.ReactNode => {
    const { startDates, selectedStartDate } = this.state;
    return startDates.map(({ date, display }) => {
      const selectedDate = date.startsWith(selectedStartDate.date);
      return (
        <option key={date} selected={selectedDate}>
          {display}
        </option>
      );
    });
  };

  renderEndDateOptions = (): React.ReactNode => {
    const { selectedStartDate, selectedEndDate } = this.state;
    return selectedStartDate.endDates.map(({ date, display, payments }) => (
      <option key={date} value={payments} selected={payments === selectedEndDate?.payments}>
        {display}
      </option>
    ));
  };

  render(): React.ReactNode {
    const { paymentDayOfTheMonth, today, rentalProtectionPolicyStatus, listingAlias } = this.props;
    const { isLoading: showSpinner, monthToMonth, selectedStartDate, showConfirmStopPayments } = this.state;
    const { wasAdjusted, date: startDate } = selectedStartDate;
    const startDateOptions = this.renderStartDateOptions();
    const endDateOptions = this.renderEndDateOptions();
    const renterMayNotHaveTime = isStartDateTooSoon(startDate, today);
    const showEdit = !showConfirmStopPayments;
    const startDateLabel = paymentDayOfTheMonth === 1 ? 'First full month of rent' : 'First full rent payment starts';

    const hasRentalProtectionPolicy =
      rentalProtectionPolicyStatus === ACTIVE || rentalProtectionPolicyStatus === PENDING_ACTIVATION;
    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header={
          hasRentalProtectionPolicy && showConfirmStopPayments ? <ZillowInsuranceServicesLogo /> : 'Payment setup'
        }
        onClickBack={this.gotoPreviousPage}
        onRequestClose={this.onClose}
        dataAutoTestId="landlord-payment-settings"
      >
        {showSpinner && <SpinnerContainer />}
        {showConfirmStopPayments && (
          <ConfirmStopPayments
            onConfirm={this.onConfirmStopPayments}
            onBack={this.onCloseStopPayments}
            hasRentalProtectionPolicy={hasRentalProtectionPolicy}
            listingAlias={listingAlias}
          />
        )}
        {showEdit && (
          <Container>
            <Section>
              <Text type="h3">How long will you be collecting rent?</Text>
            </Section>

            <Section>
              <Label className="flexDisplay">
                {startDateLabel}
                <HelpToolTip>
                  Enter your tenant&apos;s monthly rent payment date. This may be different from the payment date for
                  move-in costs, such as a security deposit.
                </HelpToolTip>
              </Label>
              <Select onChange={this.onChangeStartDate}>{startDateOptions}</Select>
              {renterMayNotHaveTime && (
                <Text type="body-small" lowlight className="TermLengthPage-StartDateSoon">
                  Your renter may not have time to set up a payment before this start date. Their first payment may take
                  a few additional days to reach you.
                </Text>
              )}

              {wasAdjusted && (
                <Text type="body-small" lowlight>
                  This month has fewer than {paymentDayOfTheMonth} days, so we selected the the last day of the month by
                  default.
                </Text>
              )}
            </Section>
            <Section>
              <Label className="flexDisplay">End date</Label>
              <Select disabled={monthToMonth} onChange={this.onChangeEndDate}>
                {endDateOptions}
              </Select>
            </Section>
            <Section>
              <Checkbox
                checked={monthToMonth}
                onChange={this.onChangeMonthToMonth}
                label="No end date/Month-to-month"
              />
            </Section>
            <Section>
              <SpacerRow>
                <FaqComponent.Landlord faqs={FaqConstants.ONBOARDING_V2.TERM_LENGTH_PAGE} />
              </SpacerRow>
            </Section>

            <Section>
              <DebouncedButton type="primary" onClick={this.onSubmit} full>
                Update
              </DebouncedButton>
            </Section>
            <Or />
            <Section>
              <Button type="tertiary" onClick={this.onShowConfirmStopPayments} full>
                Stop payments
              </Button>
            </Section>
            <Section>
              <Button type="tertiary" full data-statekey="Cancel" onClick={this.onCancel}>
                Cancel
              </Button>
            </Section>
          </Container>
        )}
      </Modal>
    );
  }
}

export default EditTermLengthModal;
